const createProgram = ({
  name,
  location,
  type,
  sourceUrl,
  overview,
  credential,
  format,
  length,
  tuition,
  admissions,
  focus,
  highlights,
  mapEmbedUrl,
}) => ({
  name,
  location,
  type,
  credential,
  format,
  length,
  tuition,
  admissions,
  summary: overview,
  badge: type,
  websiteUrl: sourceUrl,
  sourceUrl,
  specs: [
    ['Program type', type],
    ['Credential', credential],
    ['Class format', format],
    ['Program length', length],
    ['Tuition and fees', tuition],
    ['Admissions and requirements', admissions],
  ],
  paragraphs: [
    focus,
    'Program length, tuition, prerequisites, clinical requirements, application deadlines, and cohort availability can change. Confirm all current details directly with the institution before applying.',
  ],
  highlights,
  address: location,
  mapEmbedUrl,
});

export const programs = [
  createProgram({
    name: 'Sacramento City College Physical Therapist Assistant Program',
    location: '3835 Freeport Boulevard, Sacramento, CA 95822',
    type: 'Public, CAPTE-accredited community college PTA program',
    sourceUrl: 'https://www.scc.losrios.edu/physical-therapist-assistant',
    mapEmbedUrl:
      'https://www.google.com/maps?q=Sacramento+City+College+Sacramento+CA&output=embed',
    overview:
      'Sacramento City College offers a public, CAPTE-accredited Associate in Science degree for students preparing to become physical therapist assistants through a part-time evening format.',
    credential: 'Associate in Science (A.S.)',
    format:
      'Part-time evening didactic classes with integrated daytime clinical practicum experiences',
    length:
      'Four semesters and one summer session, or approximately two years',
    tuition:
      'Estimated in-state costs include approximately $644 for prerequisites, $1,518 for general education courses, $1,679 for PTA program course fees, and roughly $1,500 for books, printing, and supplies. Students should also budget for background checks, uniforms, licensure exams, and other miscellaneous fees.',
    admissions:
      'Admission is selective. Applicants must complete prerequisites such as Anatomy and Physiology, earn a minimum 3.0 GPA, complete required application steps, and meet current background-check and compliance requirements. The program enrolls one cohort annually.',
    focus:
      'Didactic evening classes are paired with integrated daytime clinical practicum experiences across regional medical facilities, giving students classroom, laboratory, and supervised patient-care preparation.',
    highlights: [
      'Public, CAPTE-accredited Associate in Science degree',
      'Part-time evening format with daytime clinical experiences',
      'Approximately $644 for prerequisites, $1,518 for general education, and $1,679 for PTA program fees',
      'Approximately $1,500 for books, printing, and supplies, plus miscellaneous compliance and licensure costs',
      'Selective annual cohort admissions with a minimum 3.0 GPA',
    ],
  }),
  createProgram({
    name: 'a2z Health Sacramento Physical Therapy Aide Technician Training',
    location: 'Sacramento, CA',
    type: 'Vocational physical therapy aide technician training',
    sourceUrl: 'https://a2zhealth.net/',
    mapEmbedUrl:
      'https://www.google.com/maps?q=a2z+Health+Sacramento+CA&output=embed',
    overview:
      'a2z Health prepares students for non-licensed support roles as physical therapy aide technicians through accessible online and in-person vocational training.',
    credential: 'Short-term career training credential',
    format:
      'Online theory instruction blended with in-person laboratory training',
    length:
      'Short-term professional training; confirm the current schedule with a2z Health',
    tuition:
      'Career training costs average approximately $2,100, inclusive of textbooks. Confirm the current tuition, payment options, materials, and any additional enrollment costs directly with a2z Health.',
    admissions:
      'Open enrollment policies apply. Review current enrollment, attendance, equipment, and any placement requirements before registering.',
    focus:
      'The curriculum is designed for rapid entry into local clinical environments and teaches essential patient support, exercise setup, and daily clinic operations without requiring a multi-year degree.',
    highlights: [
      'Non-licensed physical therapy aide technician pathway',
      'Online theory with in-person laboratory training',
      'Approximately $2,100 average cost including textbooks',
      'Open enrollment',
      'Designed for short-term entry into clinical support environments',
    ],
  }),
  createProgram({
    name: 'Unitek College Sacramento and Rancho Cordova',
    location: 'Sacramento and Rancho Cordova, CA',
    type: 'Private healthcare career training institution',
    sourceUrl: 'https://www.unitekcollege.edu/',
    mapEmbedUrl:
      'https://www.google.com/maps?q=Unitek+College+Sacramento+CA&output=embed',
    overview:
      'Unitek College offers a private healthcare training pathway focused on fast-track career readiness for clinical rehabilitation support and other medical environments.',
    credential:
      'Career training credential; verify the current program credential with Unitek College',
    format:
      'Program formats vary and may include hybrid learning, dedicated laboratory training, and clinical exposure',
    length:
      'Varies by program; confirm the current program schedule with Unitek College',
    tuition:
      'Private tuition rates apply. Request a current cost-of-attendance estimate covering tuition, fees, books, supplies, equipment, and other program expenses.',
    admissions:
      'Admissions require a high school diploma or equivalent alongside standard entrance evaluations. Confirm the current admissions process and any program-specific prerequisites.',
    focus:
      'The institution emphasizes efficient professional preparation through hybrid learning models, dedicated labs, and clinical exposure so students can target healthcare career milestones efficiently.',
    highlights: [
      'Sacramento and Rancho Cordova healthcare training locations',
      'Private vocational healthcare education',
      'Hybrid learning models may be available',
      'Dedicated laboratory and clinical exposure',
      'High school diploma or equivalent and entrance evaluations required',
    ],
  }),
  createProgram({
    name: 'Carrington College Physical Therapist Assistant Program',
    location: 'Pleasant Hill and the Greater Sacramento Region, CA',
    type: 'Private, CAPTE-accredited PTA program',
    sourceUrl:
      'https://www.carrington.edu/programs/physical-therapist-assistant/',
    mapEmbedUrl:
      'https://www.google.com/maps?q=Carrington+College+Pleasant+Hill+CA&output=embed',
    overview:
      'Carrington College offers a private, CAPTE-accredited Associate of Science physical therapist assistant program serving the Greater Sacramento region.',
    credential: 'Associate of Science',
    format:
      'Hybrid online or classroom theory, intensive on-site laboratory training, and clinical externships',
    length: 'Approximately 19 months',
    tuition:
      'The technical physical therapy assistant curriculum costs approximately $53,845, excluding general fees, materials, and other associated expenses. Confirm the current total cost of attendance and financial-aid options with Carrington College.',
    admissions:
      'Applicants must attend a mandatory information session, complete entrance assessments, and meet physical capability standards, including the ability to lift approximately 50 pounds.',
    focus:
      'The hybrid curriculum combines online or classroom theory with intensive on-site laboratory training and robust clinical externship experiences to build practical patient-care skills.',
    highlights: [
      'Private, CAPTE-accredited Associate of Science program',
      'Approximately 19-month completion timeline',
      'Hybrid theory with intensive on-site laboratory training',
      'Robust clinical externship experience',
      'Approximately $53,845 for the technical PTA curriculum before general fees and materials',
      'Mandatory information session, entrance assessments, and 50-pound lifting standard',
    ],
  }),
  createProgram({
    name: 'Gurnick Academy of Medical Arts Physical Therapist Assistant Program',
    location: 'Regional Northern California campuses serving the Sacramento area',
    type: 'Private, CAPTE-accredited PTA program',
    sourceUrl:
      'https://www.gurnick.edu/programs/physical-therapist-assistant/',
    mapEmbedUrl:
      'https://www.google.com/maps?q=Gurnick+Academy+of+Medical+Arts+California&output=embed',
    overview:
      'Gurnick Academy of Medical Arts offers a private, CAPTE-accredited Associate of Science program with full-time instruction, advanced laboratory preparation, and clinical education.',
    credential: 'Associate of Science',
    format:
      'Full-time daytime classes, industry-standard laboratory training, and multi-setting clinical placements',
    length:
      'Approximately 18 to 20 months and 1,881 total clock hours',
    tuition:
      'Private tuition and financial-planning requirements apply. Request a current cost-of-attendance estimate that includes tuition, fees, books, equipment, and clinical expenses.',
    admissions:
      'Applicants must provide high school transcripts and meet prerequisite requirements, including Anatomy and Physiology completed within the previous five years. Confirm all current admissions, compliance, and financial requirements.',
    focus:
      'The program includes 744 clinical externship hours, robust laboratory training, and clinical placements across multiple healthcare settings.',
    highlights: [
      'Private, CAPTE-accredited Associate of Science program',
      'Approximately 18 to 20 months',
      '1,881 total clock hours',
      '744 clinical externship hours',
      'Full-time daytime classes',
      'Anatomy and Physiology prerequisite generally must be completed within five years',
    ],
  }),
  createProgram({
    name: 'Ohlone College Physical Therapist Assistant Program',
    location:
      'Fremont, CA, serving the regional Bay Area and Sacramento commute',
    type: 'Public, CAPTE-accredited community college PTA program',
    sourceUrl: 'https://www.ohlone.edu/pta',
    mapEmbedUrl:
      'https://www.google.com/maps?q=Ohlone+College+Fremont+CA&output=embed',
    overview:
      'Ohlone College provides a public, CAPTE-accredited Associate in Science physical therapist assistant program for students able to commute from Sacramento or the greater Bay Area.',
    credential: 'Associate in Science',
    format:
      'Lecture theory, general education coursework, laboratory instruction, and mandatory off-campus clinical affiliations',
    length: 'Two calendar years across four semesters',
    tuition:
      'Costs follow standard California public community college tuition structures and are charged per unit. Confirm current enrollment fees, student fees, books, supplies, transportation, and clinical expenses.',
    admissions:
      'Enrollment is strictly limited to a selective cohort of students each academic year. Review current prerequisites, application deadlines, selection criteria, and clinical affiliation requirements.',
    focus:
      'The course of study blends comprehensive lecture theory, supporting general education, hands-on laboratory work, and mandatory off-campus clinical affiliations across regional medical centers.',
    highlights: [
      'Public, CAPTE-accredited Associate in Science program',
      'Two-calendar-year, four-semester course of study',
      'Public community college tuition charged per unit',
      'Selective annual cohort',
      'Mandatory off-campus clinical affiliations',
    ],
  }),
  createProgram({
    name: 'Institute of Technology Physical Therapist Assistant Program',
    location: 'Modesto, CA',
    type: 'Private physical therapist assistant Associate Degree program',
    sourceUrl: 'https://www.iot.edu/',
    mapEmbedUrl:
      'https://www.google.com/maps?q=Institute+of+Technology+Modesto+CA&output=embed',
    overview:
      'The Institute of Technology in Modesto offers a private Associate Degree physical therapist assistant program with hybrid instruction and hands-on laboratory immersion.',
    credential: 'Associate Degree',
    format:
      'Evening classes, distance education components, and hands-on laboratory training',
    length:
      'Approximately 17 to 18 months, or 75 weeks and 1,573 instructional hours',
    tuition:
      'Private institutional tuition arrangements apply. Confirm current tuition, fees, books, equipment, financing options, and total program cost directly with the institute.',
    admissions:
      'Applicants must complete high school or its equivalent, placement evaluations, and standard private-institution admissions steps. Confirm current program-specific requirements.',
    focus:
      'The hybrid curriculum focuses on functional anatomy, patient management, therapeutic exercise, and practical laboratory skills for physical therapist assistant work.',
    highlights: [
      'Private Associate Degree pathway',
      'Approximately 17 to 18 months',
      '75 weeks and 1,573 instructional hours',
      'Evening and distance education options',
      'Hands-on laboratory immersion',
      'High school completion and placement evaluations required',
    ],
  }),
  createProgram({
    name: 'Mendocino College Physical Therapist Assistant Program',
    location: 'Ukiah, CA, serving regional Northern California',
    type: 'Public, CAPTE-accredited community college PTA program',
    sourceUrl: 'https://www.mendocino.edu/',
    mapEmbedUrl:
      'https://www.google.com/maps?q=Mendocino+College+Ukiah+CA&output=embed',
    overview:
      'Mendocino College offers a public, CAPTE-accredited Associate in Science physical therapist assistant pathway that combines prerequisites, general education, core PTA coursework, and clinical education.',
    credential: 'Associate in Science',
    format:
      'Prerequisite and general education coursework followed by classroom, laboratory, and clinical education',
    length:
      'Six semesters: two semesters of prerequisites and general education followed by four semesters of core PTA curriculum',
    tuition:
      'Costs follow standard California public community college tuition schedules. Confirm current enrollment fees, books, supplies, transportation, and clinical expenses.',
    admissions:
      'Admission uses a multi-selection points system that evaluates completed prerequisite GPA and healthcare work or volunteer experience. Review the current application process, prerequisites, and selection criteria.',
    focus:
      'The final year includes two full-time, eight-week clinical education practicums, giving students concentrated supervised experience in clinical environments.',
    highlights: [
      'Public, CAPTE-accredited Associate in Science pathway',
      'Six-semester educational sequence',
      'Two prerequisite and general education semesters',
      'Four semesters of core PTA curriculum',
      'Two full-time, eight-week clinical practicums',
      'Selection points include prerequisite GPA and healthcare experience',
    ],
  }),
  createProgram({
    name: 'Charles A. Jones Career and Education Center Physical Therapy Aide Training',
    location: 'Sacramento, CA',
    type: 'Public adult education physical therapy aide training',
    sourceUrl: 'https://www.sanjuan.edu/',
    mapEmbedUrl:
      'https://www.google.com/maps?q=Charles+A.+Jones+Career+and+Education+Center+Sacramento+CA&output=embed',
    overview:
      'Charles A. Jones Career and Education Center offers an online physical therapy aide training pathway for students seeking foundational preparation for non-clinical rehabilitation support roles.',
    credential:
      'Preparation for the American Medical Certification Association Physical Therapy Technician/Aide Certification (PTTC) exam',
    format: 'Flexible online instruction',
    length: 'Approximately nine months and 202 course hours',
    tuition:
      'Pricing follows public adult education and vocational training guidelines. Confirm current tuition, registration, materials, and certification-exam costs directly with the center.',
    admissions:
      'Applicants need a government-issued photo ID and a high school diploma or equivalent for certification eligibility. Verify current enrollment and certification requirements.',
    focus:
      'The curriculum teaches foundational non-clinical rehabilitation concepts, medical terminology, patient positioning, and clinical sanitation while preparing students to sit for the AMCA PTTC exam.',
    highlights: [
      'Public adult education option in Sacramento',
      'Flexible online learning format',
      'Approximately nine months and 202 course hours',
      'Covers medical terminology, patient positioning, and clinical sanitation',
      'Prepares students for the AMCA Physical Therapy Technician/Aide Certification exam',
      'Government-issued photo ID and high school diploma or equivalent required for certification eligibility',
    ],
  }),
];

export const resources = [
  {
    title: 'American Physical Therapy Association',
    description:
      'Explore physical therapy education, professional standards, advocacy, and career resources.',
    url: 'https://www.apta.org/',
  },
  {
    title: 'Federation of State Boards of Physical Therapy',
    description:
      'Review physical therapist assistant licensure and National Physical Therapy Examination resources.',
    url: 'https://www.fsbpt.org/',
  },
  {
    title: 'California Physical Therapy Board',
    description:
      'Find California licensing, renewal, rules, and practice information for PTAs.',
    url: 'https://www.ptbc.ca.gov/',
  },
];